import { FileProcessingResultStatus } from '@atlas/types'
import { logger } from '@/lib/logger'
import type { UploadItem } from './upload-item.types'
import { UploadItemStatus } from './upload-status'

const log = logger.child('upload-catch-up')

/**
 * Delays after confirm at which to re-read the file, covering the window before
 * this file's realtime subscription exists. Backs off quickly and stops: push
 * owns everything beyond this, so these are a startup patch, not a poll.
 */
const CATCH_UP_DELAYS_MS = [400, 1200, 3000] as const

/** Concerns still pending or running — zero means nothing more is coming. */
export function outstandingConcernCount(item: UploadItem): number {
	return Object.values(item.concerns).filter(
		(entry) =>
			entry.status === FileProcessingResultStatus.PENDING || entry.status === FileProcessingResultStatus.RUNNING,
	).length
}

/**
 * Re-reads a just-confirmed file a few times, stopping as soon as it settles or
 * has nothing outstanding.
 *
 * Exists because a concern can complete before the browser has subscribed for
 * that file id: the server often finishes within a few hundred milliseconds of
 * confirm, MQTT does not replay, and with the connection up the poll fallback is
 * off — so the tile would sit on "Classifying…" while the row reads `completed`.
 *
 * Push remains the primary path; this only covers the gap before it is listening.
 */
export function scheduleConfirmCatchUp(
	itemId: string,
	fileId: string,
	lookup: (id: string) => UploadItem | undefined,
	refresh: (fileId: string) => void,
): void {
	for (const delay of CATCH_UP_DELAYS_MS) {
		setTimeout(() => {
			const item = lookup(itemId)
			// Gone, already settled, or nothing left to wait for.
			if (!item || item.status !== UploadItemStatus.CONFIRMED) return
			// An EMPTY concern map means "nothing reported yet", not "nothing to
			// wait for" — the same reading `isSettled` takes. Bailing on it skipped
			// the catch-up in precisely the window it exists to cover: a work order
			// with an empty tuple sends no classify, so confirm returns
			// `processingResults: {}`, all three ticks returned early, and the tile
			// sat spinning until the 30s safety-net poll.
			const reported = Object.keys(item.concerns).length > 0
			if (reported && outstandingConcernCount(item) === 0) return
			log.debug('catch-up refresh', { fileId, delay })
			refresh(fileId)
		}, delay)
	}
}
